import { appConfig } from "@/config/appConfig";

export interface ApiError {
  status: number;
  message: string;
  traceId?: string;
}

type TokenProvider = () => Promise<string | null>;

let tokenProvider: TokenProvider = async () => null;

export function setTokenProvider(provider: TokenProvider): void {
  tokenProvider = provider;
}

/**
 * Current AAD access token (or null in dev/stub). Use this when you need to make a raw `fetch`
 * that `apiFetch` can't cover — e.g. streaming or binary/blob responses like the CSV export —
 * and must still send `Authorization: Bearer`.
 */
export function getAuthToken(): Promise<string | null> {
  return tokenProvider();
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await tokenProvider();
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const url = path.startsWith("http") ? path : `${appConfig.apiBaseUrl}${path}`;
  const response = await fetch(url, { ...init, headers });

  if (!response.ok) {
    const traceId = response.headers.get("x-trace-id") ?? undefined;
    const text = await response.text().catch(() => "");
    const error: ApiError = {
      status: response.status,
      message: text || response.statusText,
      traceId,
    };
    throw error;
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}
